'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { Search, Heart } from 'lucide-react';
import { useDisplayCurrency } from '@/tenants/_shared/hooks';
import { useGeneralDonationStrategy } from '@/tenants/_shared/hooks/donation-strategies';
import { usePaymentReturn } from '@/tenants/_shared/hooks/usePaymentReturn';
import { DonationFormModal, DonationAmountCard } from '@/tenants/_shared/components';
import { trpc } from '@/lib/trpc';
import { buildLocalePath, type Locale } from '@/i18n/config';
import { useAuth } from '@/hooks/use-auth';
import { useSearchStore } from '@/stores/search-store';
import { Input } from '@/components/ui/input/input';
import { Button } from '@/components/ui/button';

type ActiveCampaign = {
  id: string;
  name: string;
};

type DonateTab = 'search' | 'general';

export function DonatePage() {
  const locale = useLocale() as Locale;
  const t = useTranslations('donate');
  const tCommon = useTranslations('common');
  const displayCurrency = useDisplayCurrency();
  const router = useRouter();
  const { startSearchTransition } = useSearchStore();
  const { isAuthenticated, entrant, isEntrant } = useAuth({ hydrateFromQuery: true });
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Fetch active campaign for this tenant
  const campaignQuery = trpc.publicDonation.getActiveCampaign.useQuery();
  const campaign = campaignQuery.data as ActiveCampaign | null | undefined;
  const isCampaignLoading = campaignQuery.isLoading;
  const isCampaignError = campaignQuery.isError;

  // Redirect return detection (bank redirect / 3DS)
  const {
    returnDonationId,
    redirectResult,
    sessionId: returnSessionId,
    campaignId: returnCampaignId,
    recoveryToken,
    isLocalStorageRecovery,
  } = usePaymentReturn();

  // Use returnCampaignId as fallback for redirect returns
  const activeCampaignId = campaign?.id ?? returnCampaignId ?? undefined;

  // General donation strategy
  const strategy = useGeneralDonationStrategy(activeCampaignId, displayCurrency.code);

  // Modal state
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [donateInitialAmount, setDonateInitialAmount] = useState<number | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<DonateTab>('search');
  const [searchQuery, setSearchQuery] = useState('');

  // Focus search input when switching to search tab
  useEffect(() => {
    if (activeTab === 'search') {
      // Small delay to allow DOM to render
      const timeout = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(timeout);
    }
  }, [activeTab]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (trimmed) {
      startSearchTransition(trimmed);
      router.push(`${buildLocalePath(locale, '/search')}?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const tabs: { id: DonateTab; icon: typeof Search; label: string; description: string }[] = [
    {
      id: 'search',
      icon: Search,
      label: t('tabs.searchTitle'),
      description: t('tabs.searchDescription'),
    },
    {
      id: 'general',
      icon: Heart,
      label: t('tabs.generalTitle'),
      description: t('tabs.generalDescription'),
    },
  ];

  return (
    <>
      <main className="flex-1">
        {/* Hero / Cover Section */}
        <section className="relative h-[280px] md:h-[460px]">
          <Image
            src="/images/uwr/HeroImageUWR.jpg"
            alt={t('makeADonation')}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white">{t('makeADonation')}</h1>
            </div>
          </div>
        </section>

        {/* Tab Cards */}
        <section className="py-10 md:py-14 bg-stone-50">
          <div className="container max-w-5xl">
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8 max-w-md mx-auto">
              {tabs.map(({ id, icon: Icon, label, description }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`group relative flex flex-col items-center text-center rounded-xl p-5 md:p-6 border-2 transition-all duration-200 cursor-pointer ${
                    activeTab === id
                      ? 'border-uwr-orange bg-uwr-orange/10 text-stone-900 shadow-lg'
                      : 'border-stone-200 bg-white text-stone-900 hover:border-uwr-orange/50 hover:shadow-md'
                  }`}
                >
                  <Icon
                    className={`w-8 h-8 md:w-10 md:h-10 mb-3 transition-colors ${
                      activeTab === id
                        ? 'text-uwr-orange'
                        : 'text-stone-400 group-hover:text-uwr-orange'
                    }`}
                  />
                  <h3
                    className={`font-semibold text-sm md:text-base mb-1 ${
                      activeTab === id ? 'text-stone-900' : 'text-stone-900'
                    }`}
                  >
                    {label}
                  </h3>
                  <p
                    className={`text-xs hidden md:block ${
                      activeTab === id ? 'text-stone-500' : 'text-stone-500'
                    }`}
                  >
                    {description}
                  </p>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="max-w-2xl mx-auto">
              {/* Search to Donate */}
              {activeTab === 'search' && (
                <div className="bg-white rounded-xl p-6 md:p-8 border border-stone-200 shadow-sm">
                  {isAuthenticated && isEntrant() && entrant ? (
                    <>
                      <h2 className="text-xl font-bold text-stone-900 mb-2">
                        {t('tabs.searchLoggedInTitle')}
                      </h2>
                      <p className="text-stone-600 text-sm mb-6">
                        {t('tabs.searchLoggedInDescription')}
                      </p>
                      <Link
                        href={buildLocalePath(locale, `/fundraiser/${entrant.username}`)}
                        className="flex items-center gap-4 p-4 rounded-lg border border-stone-200 hover:border-uwr-orange hover:shadow-md transition-all"
                      >
                        {entrant.profileImageUrl ? (
                          <Image
                            src={entrant.profileImageUrl}
                            alt={`${entrant.firstName} ${entrant.lastName}`}
                            width={56}
                            height={56}
                            className="rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-uwr-orange/10 flex items-center justify-center text-uwr-orange font-bold text-lg">
                            {entrant.firstName.charAt(0)}
                            {entrant.lastName.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 text-left">
                          <p className="font-semibold text-stone-900">
                            {entrant.firstName} {entrant.lastName}
                          </p>
                          <p className="text-sm text-uwr-orange">{t('tabs.viewMyProfile')}</p>
                        </div>
                      </Link>
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-stone-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-white text-stone-400">
                            {t('tabs.orSearchOthers')}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-xl font-bold text-stone-900 mb-2">
                        {t('tabs.searchTitle')}
                      </h2>
                      <p className="text-stone-600 text-sm mb-6">{t('tabs.searchInstructions')}</p>
                    </>
                  )}
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t('tabs.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setSearchQuery(e.target.value)
                        }
                        className="h-14 pl-12 pr-4 text-lg bg-stone-50 border-stone-200 focus-visible:ring-uwr-orange"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!searchQuery.trim()}
                      className="w-full mt-4 h-12 bg-uwr-orange text-white hover:bg-uwr-orange-hover disabled:opacity-50 font-semibold"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      {t('tabs.searchButton')}
                    </Button>
                  </form>
                </div>
              )}

              {/* General Donation */}
              {activeTab === 'general' && (
                <div>
                  {isCampaignLoading ? (
                    <p className="text-center text-stone-400">{tCommon('loading')}</p>
                  ) : isCampaignError ? (
                    <div className="text-center py-8">
                      <p className="text-stone-500">{tCommon('error')}</p>
                    </div>
                  ) : !campaign ? (
                    <div className="text-center py-8">
                      <p className="text-stone-500">{t('noCampaign')}</p>
                    </div>
                  ) : (
                    <DonationAmountCard
                      recipientName={campaign.name}
                      currency={strategy.currency}
                      onDonate={(amount) => {
                        setDonateInitialAmount(amount);
                        setIsDonateModalOpen(true);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Donation Modal — auto-opens on redirect return via internal useEffect */}
      {(campaign || returnDonationId) && (
        <DonationFormModal
          open={isDonateModalOpen}
          onOpenChange={(open) => {
            setIsDonateModalOpen(open);
            if (!open) setDonateInitialAmount(null);
          }}
          initialAmount={donateInitialAmount}
          recipientName={campaign?.name ?? ''}
          strategy={strategy}
          returnDonationId={returnDonationId ?? undefined}
          redirectResult={redirectResult}
          returnSessionId={returnSessionId}
          recoveryToken={recoveryToken}
          isLocalStorageRecovery={isLocalStorageRecovery}
        />
      )}
    </>
  );
}
